import { useEffect } from "react";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { AppError, Loader } from "@bfc-software/pallet";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { AppHeader } from "@bfc-software/bfc-web-ui/ui/shared/app-header";
import { DeliverManageSidebar } from "./-components/deliver-manage-sidebar";

import { useManageTenant } from "../-data/use-manage-tenant";

/**
 * Manage Warehouse's shell: the header, the navigation rail, and the tenant
 * (warehouse) the screens below manage.
 *
 * Unlike Outbound, nothing here touches the session: Deliver's own service
 * scopes by tenant, and the AAA calls carry the tenant's org and warehouse
 * explicitly. The URL names the tenant and that is the whole of the scope.
 *
 * A tenant the caller cannot see goes back to the dashboard rather than
 * rendering an empty management screen that reads as an empty warehouse.
 */
export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/deliver/manage/$tenant",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const params = Route.useParams();
  const navigate = useNavigate();
  const { status } = useManageTenant();
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.deliver,
  });

  useEffect(() => {
    if (status !== "unknown") return;
    navigate({
      to: "/$lang/orgs/$org/deliver",
      params: { lang: params.lang, org: params.org },
      replace: true,
    });
  }, [status, navigate, params.lang, params.org]);

  return (
    <>
      <AppHeader />
      <div className="flex flex-1 min-h-0 border-t border-outline-variant">
        <DeliverManageSidebar />
        <div className="flex flex-1 flex-col min-w-0 min-h-0 overflow-auto bg-background">
          {status === "ready" ? (
            <Outlet />
          ) : status === "error" ? (
            <AppError
              description={t("warehouses.failed")}
              resolution={t("manage.failureSupport")}
            />
          ) : (
            <Loader type="page" />
          )}
        </div>
      </div>
    </>
  );
}
